import React, { useState, useEffect, useCallback, useRef } from 'react';
import APIService from '../services/api';

/**
 * Assistant « informations contrat » — un passage, lot par lot, personne par
 * personne, pour réunir ce qui manque à la génération des contrats.
 *
 * Pourquoi un assistant plutôt qu'un formulaire par fiche : les informations
 * demandées ne servent qu'au contrat, elles sont réclamées pour tout un AO en
 * même temps, et les chercher fiche par fiche fait perdre le fil. Ici on ne
 * quitte jamais l'écran, et la progression est visible.
 *
 * Deux natures de champs, deux destinations :
 *  - civilité et nationalité appartiennent à la PERSONNE : elles
 *    valent pour tous ses contrats et vont sur sa fiche ;
 *  - fonction et salaire du contrat appartiennent au POSITIONNEMENT : la même
 *    personne peut être proposée sur deux lots à des conditions différentes.
 *
 * Rien n'est annoncé comme enregistré sans preuve : après écriture, la fiche
 * est relue et les valeurs comparées. Un champ que le serveur n'a pas conservé
 * est nommé, à l'écran et dans le récapitulatif final — ces champs sont
 * nouveaux, et un « enregistré » de complaisance ferait retravailler pour rien.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', borderRadius: '9px',
  border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)',
  fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
};
const btnFort = { ...btn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' };
const inp = {
  width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: '8px',
  border: '1px solid var(--input-border)', background: 'var(--input-bg, var(--surface))',
  color: 'var(--text-strong)', fontFamily: 'inherit', fontSize: '13px', outline: 'none',
};
const lbl = { display: 'block', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '5px' };

// Champs de la personne : ils vont sur la fiche et valent pour tous ses contrats.
const CHAMPS_PERSONNE = [
  { cle: 'civilite', label: 'Civilité', type: 'choix', options: ['Monsieur', 'Madame'] },
  // Facultative (03.11) : on peut la laisser vide, elle n'est pas réclamée.
  { cle: 'nationalite', label: 'Nationalité (facultatif)', type: 'texte', facultatif: true },
];
// Champs du positionnement : propres au couple candidat × besoin.
// `cleApi` : le nom exact que le serveur accepte à l'écriture quand il diffère
// du nom d'écran. PATCH /candidates/:id/besoins/:besoinId ignore en silence
// toute clé inconnue — la date partait sous « date_debut » et rien n'était
// écrit, sans erreur. La relecture de preuve ci-dessous attrape désormais ce
// genre d'écart, mais autant envoyer le bon nom.
const CHAMPS_POSITION = [
  { cle: 'fonction', label: 'Fonction (titre du poste)', type: 'texte', placeholder: 'Architecte sécurité' },
  { cle: 'salaire_contrat', label: 'Salaire du contrat', type: 'texte', placeholder: "95'000 CHF" },
  { cle: 'date_debut', cleApi: 'date_debut_contrat', label: 'Date de démarrage', type: 'date' },
  { cle: 'type_contrat', label: 'Type de contrat', type: 'texte', placeholder: 'CDI, CDD, mission…' },
];

const TOUS = [...CHAMPS_PERSONNE, ...CHAMPS_POSITION];

/** Première valeur non vide parmi plusieurs graphies possibles. */
const premier = (o, ...cles) => {
  for (const c of cles) {
    const v = o?.[c];
    if (v != null && String(v).trim() !== '') return String(v);
  }
  return '';
};

/**
 * Intitulé de poste sans la référence du lot.
 *
 * Un lot s'appelle « Analyste métier junior 26-01-7705-001 » : la référence
 * identifie le lot, mais elle n'a rien à faire dans la fonction inscrite sur un
 * contrat de travail. On retire donc le groupe de chiffres et tirets final.
 */
const titrePoste = (v) => String(v || '')
  .replace(/[\s—–-]*(?:r[ée]f\.?\s*:?\s*)?\b\d{2,}(?:[-.\/]\d+){1,}\b\s*$/i, '')
  .trim() || String(v || '').trim();

/** Une date au format attendu par <input type="date">. */
const enDateInput = (v) => {
  const s = String(v || '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const fr = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (fr) return `${fr[3]}-${fr[2].padStart(2, '0')}-${fr[1].padStart(2, '0')}`;
  return '';
};

const memeValeur = (a, b) => String(a ?? '').trim() === String(b ?? '').trim();

export default function AoInfosContrat({ aoId, personnes, showNotification, onClose }) {
  const [idx, setIdx] = useState(0);
  const [form, setForm] = useState(null);          // null = chargement
  const [busy, setBusy] = useState(false);
  const [etat, setEtat] = useState({});            // candidateId → 'ok' | 'partiel' | 'passe'
  const [nonConserves, setNonConserves] = useState({});
  const [positionAbsente, setPositionAbsente] = useState(false); // route positionnement non servie
  const [fini, setFini] = useState(false);
  const cache = useRef({});
  // Valeurs par défaut de l'organisation (GET /config/contrat-defauts). Vide
  // tant que rien n'a été posé : les champs restent alors à remplir, ce qui est
  // plus honnête qu'un défaut inventé par l'écran.
  const [defauts, setDefauts] = useState({});
  // Bascule une seule fois : les défauts arrivent après le premier rendu, le
  // formulaire doit alors se reconstruire — mais une seule fois, sinon il
  // effacerait une saisie en cours.
  const [defautsPrets, setDefautsPrets] = useState(false);

  /**
   * Une personne = un profil, quels que soient ses lots.
   *
   * Un candidat proposé sur trois lots ne doit pas être présenté trois fois :
   * sa civilité, sa date de naissance et son adresse sont les mêmes partout, et
   * les redemander donne l'impression de recommencer. Seules la fonction et le
   * salaire du contrat changent d'un lot à l'autre — ils ont donc une ligne par
   * lot, à l'intérieur du même profil.
   */
  const profils = React.useMemo(() => {
    const m = new Map();
    (personnes || []).forEach(x => {
      const k = String(x.candidateId);
      if (!m.has(k)) m.set(k, { candidateId: x.candidateId, nom: x.nom, lots: [] });
      m.get(k).lots.push({ besoinId: x.besoinId, lot: x.lot, poste: x.poste });
    });
    return [...m.values()];
  }, [personnes]);

  const total = profils.length;
  const p = profils[idx] || null;
  const cle = p ? String(p.candidateId) : '';

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/config/contrat-defauts`, { headers: authH() });
        if (!r.ok) return;
        const j = await r.json().catch(() => ({}));
        if (!annule) { setDefauts(j.valeurs || j.data?.valeurs || {}); setDefautsPrets(true); }
      } catch (e) { /* pas de défaut : les champs restent vides */ }
    })();
    return () => { annule = true; };
  }, []);

  const lireFiche = useCallback(async (candidateId) => {
    if (cache.current[candidateId]) return cache.current[candidateId];
    try {
      const r = await fetch(`${BASE}/candidats/vue/${candidateId}`, { headers: authH() });
      if (!r.ok) return {};
      const j = await r.json().catch(() => ({}));
      const f = j.data || j.candidat || j;
      cache.current[candidateId] = f;
      return f;
    } catch (e) { return {}; }
  }, []);

  /**
   * Formulaire d'un profil, depuis sa fiche et les défauts de l'organisation.
   * Une seule construction pour l'écran et pour « Tout enregistrer » : le clic
   * unique enregistre exactement ce que l'écran aurait montré.
   */
  const construireForm = useCallback((prof, f) => {
    const posDe = (besoinId) => (f.positionnements || f.besoins || [])
      .find(x => String(x.besoin_id ?? x.besoinId ?? x.id) === String(besoinId)) || {};
    return {
      // Civilité : la fiche, sinon le défaut de l'organisation (Configuration >
      // Contrats) — jamais une valeur écrite dans l'écran.
      civilite: premier(f, 'civilite', 'civility') || premier(defauts, 'civility', 'civilite'),
      nationalite: premier(f, 'nationalite', 'nationality'),
      // Une entrée par lot. La fonction part du titre du lot : c'est
      // l'intitulé exact du besoin, donc la valeur juste presque à chaque fois.
      lots: Object.fromEntries(prof.lots.map(l => {
        const pos = posDe(l.besoinId);
        return [String(l.besoinId), {
          fonction: premier(pos, 'fonction', 'poste_propose') || titrePoste(l.poste) || premier(f, 'titre'),
          // C'est le montant qui sera écrit sur le contrat. Les anciennes
          // graphies servent de pré-remplissage tant que des données les
          // portent encore, mais l'écriture se fait sur salaire_contrat.
          salaire_contrat: premier(pos, 'salaire_contrat', 'salaire_propose', 'salaire_proposition', 'salaire')
            || premier(defauts, 'annualSalary', 'salaire_contrat', 'salary', 'salaire'),
          // Le positionnement l'emporte ; à défaut, la valeur de l'organisation.
          // Une date écrite en dur vieillit et finit par se glisser dans un
          // contrat sans que personne ne s'en aperçoive.
          date_debut: enDateInput(premier(pos, 'date_debut_contrat', 'date_debut', 'date_demarrage', 'startDate'))
            || enDateInput(premier(defauts, 'date_debut', 'startDate', 'date_demarrage')),
          type_contrat: premier(pos, 'type_contrat', 'employeeType')
            || premier(defauts, 'type_contrat', 'employeeType'),
        }];
      })),
    };
  }, [defauts]);

  // Chargement de la personne courante, et de la suivante en avance : la
  // saisie ne doit jamais attendre le réseau entre deux profils.
  useEffect(() => {
    if (!p) return;
    let annule = false;
    setForm(null);
    (async () => {
      const f = await lireFiche(p.candidateId);
      if (annule) return;
      setForm(construireForm(p, f));
      const suivant = profils[idx + 1];
      if (suivant) lireFiche(suivant.candidateId);
    })();
    return () => { annule = true; };
    // Dépendances volontairement réduites à l'identité de la personne : une
    // liste recréée à l'identique par le parent ne doit pas relancer le
    // chargement, ni effacer une saisie en cours.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, p?.candidateId, defautsPrets]);

  const manquants = form
    ? [
      ...CHAMPS_PERSONNE.filter(c => !c.facultatif && !String(form[c.cle] || '').trim()).map(c => c.label),
      ...CHAMPS_POSITION.filter(c => (p?.lots || []).some(l => !String(form.lots?.[String(l.besoinId)]?.[c.cle] || '').trim())).map(c => c.label),
    ]
    : [];

  // `enLot` : appelé par « Tout enregistrer », qui tient lui-même l'état occupé.
  const enregistrer = async (p, form, { enLot = false } = {}) => {
    if (!p || !form) return false;
    const cle = String(p.candidateId);
    if (!enLot) setBusy(true);
    const perdus = [];
    try {
      // 1) La personne. On n'envoie que ce qui est renseigné : un champ vide ne
      // doit pas écraser une valeur déjà présente sur la fiche.
      const corpsPersonne = {};
      CHAMPS_PERSONNE.forEach(c => { const v = String(form[c.cle] || '').trim(); if (v) corpsPersonne[c.cle] = v; });
      if (Object.keys(corpsPersonne).length) {
        const r = await fetch(`${BASE}/candidats/vue/${p.candidateId}`, {
          method: 'PUT', headers: jsonH(), body: JSON.stringify(corpsPersonne),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        // Preuve : on relit et on compare. Ces champs sont nouveaux ; un
        // serveur qui les ignore répond 200 sans rien conserver.
        delete cache.current[p.candidateId];
        const relu = await lireFiche(p.candidateId);
        Object.keys(corpsPersonne).forEach(k => {
          const alias = { civilite: ['civility'], adresse: ['address'], nationalite: ['nationality'] };
          const lu = k === 'date_naissance'
            ? enDateInput(premier(relu, 'date_naissance', 'dateOfBirth', 'birth_date'))
            : premier(relu, k, ...(alias[k] || []));
          if (!memeValeur(lu, corpsPersonne[k])) perdus.push(TOUS.find(c => c.cle === k)?.label || k);
        });
      }

      // 2) Un appel par lot : la fonction et le salaire diffèrent d'un lot à
      // l'autre. Route pas encore servie partout — son absence est annoncée une
      // fois, sans faire échouer le reste de la saisie.
      const ecrits = [];   // ce qui a été envoyé, pour la relecture de preuve
      for (const l of p.lots) {
        if (l.besoinId == null) continue;
        const v = form.lots?.[String(l.besoinId)] || {};
        const corpsPos = {};
        CHAMPS_POSITION.forEach(c => {
          let t = String(v[c.cle] || '').trim();
          if (c.type === 'date' && t) t = t.slice(0, 10);
          if (t) corpsPos[c.cleApi || c.cle] = t;
        });
        if (!Object.keys(corpsPos).length) continue;
        const r2 = await fetch(`${BASE}/candidates/${p.candidateId}/besoins/${l.besoinId}`, {
          method: 'PATCH', headers: jsonH(), body: JSON.stringify(corpsPos),
        });
        if (r2.status === 404 || r2.status === 405 || r2.status === 501) {
          setPositionAbsente(true);
          CHAMPS_POSITION.forEach(c => { if (corpsPos[c.cleApi || c.cle]) perdus.push(c.label); });
          break;   // la route manque pour tous les lots, inutile d'insister
        }
        if (!r2.ok) throw new Error(`HTTP ${r2.status}`);
        ecrits.push({ besoinId: l.besoinId, corpsPos });
      }

      // Preuve pour les lots aussi : la route ignore en silence toute clé
      // qu'elle ne connaît pas — un 200 ne dit pas que la valeur est écrite.
      // On relit la fiche et on compare ce qui a réellement été conservé.
      if (ecrits.length) {
        delete cache.current[p.candidateId];
        const relu = await lireFiche(p.candidateId);
        const posRelue = (besoinId) => (relu.positionnements || relu.besoins || [])
          .find(x => String(x.besoin_id ?? x.besoinId ?? x.id) === String(besoinId)) || {};
        for (const e of ecrits) {
          const pos = posRelue(e.besoinId);
          CHAMPS_POSITION.forEach(c => {
            const envoye = e.corpsPos[c.cleApi || c.cle];
            if (envoye == null) return;
            const lu = c.type === 'date'
              ? enDateInput(premier(pos, c.cleApi || c.cle, c.cle, 'date_demarrage', 'startDate'))
              : premier(pos, c.cleApi || c.cle, c.cle);
            if (!memeValeur(lu, envoye) && !perdus.includes(c.label)) perdus.push(c.label);
          });
        }
      }

      setNonConserves(m => (perdus.length ? { ...m, [cle]: perdus } : m));
      setEtat(m => ({ ...m, [cle]: perdus.length ? 'partiel' : 'ok' }));
      return true;
    } catch (e) {
      showNotification?.(`Enregistrement impossible${enLot ? ` (${p.nom})` : ''} : ${e.message}`, 'error');
      return false;
    } finally { if (!enLot) setBusy(false); }
  };

  const avancer = () => {
    if (idx + 1 < total) setIdx(idx + 1); else setFini(true);
  };
  const suivant = async () => { if (await enregistrer(p, form)) avancer(); };

  /**
   * Tout enregistrer en un clic : le profil affiché avec ce qui est saisi, puis
   * chaque profil suivant avec ce que l'écran lui aurait proposé (fiche, titre
   * du lot, défauts de l'organisation). Même écriture, même relecture de
   * preuve que le bouton profil par profil ; un échec n'arrête pas les autres.
   */
  const [lot, setLot] = useState(null);           // { fait, total, nom } pendant le passage
  const toutEnregistrer = async () => {
    if (!p || !form) return;
    setBusy(true);
    const reste = profils.slice(idx);
    try {
      for (let i = 0; i < reste.length; i++) {
        const prof = reste[i];
        setLot({ fait: i, total: reste.length, nom: prof.nom });
        const f = i === 0 ? form : construireForm(prof, await lireFiche(prof.candidateId));
        const ok = await enregistrer(prof, f, { enLot: true });
        if (!ok) setEtat(m => ({ ...m, [String(prof.candidateId)]: 'echec' }));
      }
    } finally {
      setLot(null);
      setBusy(false);
      setFini(true);
    }
  };
  const passer = () => { setEtat(m => ({ ...m, [cle]: 'passe' })); avancer(); };

  const compte = (v) => Object.values(etat).filter(x => x === v).length;
  const champsPerdus = [...new Set(Object.values(nonConserves).flat())];

  const corps = () => {
    if (fini) {
      return (
        <div style={{ padding: '10px 2px' }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)', marginBottom: '10px' }}>Passage terminé</div>
          <div style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.7 }}>
            {compte('ok')} profil{compte('ok') > 1 ? 's' : ''} enregistré{compte('ok') > 1 ? 's' : ''}
            {compte('partiel') > 0 && <> · {compte('partiel')} partiellement</>}
            {compte('passe') > 0 && <> · {compte('passe')} passé{compte('passe') > 1 ? 's' : ''}</>}
            {compte('echec') > 0 && <> · {compte('echec')} en échec</>}
            {' '}sur {total}.
          </div>
          {champsPerdus.length > 0 && (
            <div style={{ marginTop: '14px', padding: '11px 13px', borderRadius: '10px', background: 'rgba(217,119,6,0.10)', border: '1px solid rgba(217,119,6,0.30)' }}>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#b45309', marginBottom: '4px' }}>
                <BI icon="exclamation-triangle" style={{ marginRight: '6px' }} />Non conservé par le serveur
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.6 }}>
                {champsPerdus.join(', ')} — ces informations ne sont pas encore stockées. Elles arrivent côté serveur ;
                d'ici là, elles restent à saisir au moment de générer les contrats.
              </div>
            </div>
          )}
        </div>
      );
    }
    if (!p) return <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13px' }}>Aucun candidat à compléter.</div>;
    if (!form) return <div style={{ padding: '30px 0', textAlign: 'center', color: 'var(--text-faint)' }}><BI icon="arrow-repeat" style={{ animation: 'aoi-spin .7s linear infinite' }} /></div>;
    return (
      <>
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-strong)' }}>{p.nom}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>
            {p.lots.length > 1 ? `${p.lots.length} lots` : p.lots[0]?.lot}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '11px' }}>
          {CHAMPS_PERSONNE.map(c => (
            <div key={c.cle}>
              <label style={lbl}>{c.label}</label>
              {c.type === 'choix' ? (
                <div style={{ display: 'flex', gap: '6px' }}>
                  {c.options.map(o => (
                    <button key={o} onClick={() => setForm(f => ({ ...f, [c.cle]: f[c.cle] === o ? '' : o }))}
                      style={{ ...btn, flex: 1, justifyContent: 'center', padding: '9px 10px', ...(form[c.cle] === o ? { background: 'var(--accent)', color: 'var(--accent-fg)', border: 'none' } : {}) }}>{o}</button>
                  ))}
                </div>
              ) : (
                <input type={c.type === 'date' ? 'date' : 'text'} value={form[c.cle] || ''} placeholder={c.placeholder || ''}
                  onChange={e => setForm(f => ({ ...f, [c.cle]: e.target.value }))} style={inp} />
              )}
            </div>
          ))}
        </div>

        {/* Ce qui change d'un lot à l'autre, et seulement cela. */}
        {p.lots.map(l => (
          <div key={String(l.besoinId)} style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--hair)' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-2)', marginBottom: '8px' }}>{l.lot}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '11px' }}>
              {CHAMPS_POSITION.map(c => (
                <div key={c.cle}>
                  <label style={lbl}>{c.label}</label>
                  <input type={c.type === 'date' ? 'date' : 'text'} placeholder={c.placeholder || ''}
                    value={form.lots?.[String(l.besoinId)]?.[c.cle] || ''}
                    onChange={e => setForm(f => ({
                      ...f,
                      lots: { ...f.lots, [String(l.besoinId)]: { ...(f.lots?.[String(l.besoinId)] || {}), [c.cle]: e.target.value } },
                    }))} style={inp} />
                </div>
              ))}
            </div>
          </div>
        ))}
        {manquants.length > 0 && (
          <div style={{ marginTop: '11px', fontSize: '11.5px', color: 'var(--text-3)' }}>
            Encore vide : {manquants.join(', ')}. Vous pouvez enregistrer quand même et repasser plus tard.
          </div>
        )}
        {positionAbsente && (
          <div style={{ marginTop: '11px', fontSize: '11.5px', color: 'var(--text-2)', padding: '9px 11px', borderRadius: '9px', background: 'var(--code-bg)' }}>
            Fonction et salaire du contrat ne sont pas encore stockés par lot — ils arrivent côté serveur. Le reste est bien enregistré.
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <style>{'@keyframes aoi-spin{to{transform:rotate(360deg)}}'}</style>
      <div onClick={busy ? undefined : onClose} style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(8,9,12,0.5)' }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 81, width: 'min(620px,94vw)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', boxShadow: '0 24px 60px rgba(8,9,12,0.4)', padding: '20px 22px' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
          <span style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)', flex: 1 }}>
            Informations pour les contrats
          </span>
          <button onClick={onClose} disabled={busy} style={{ ...btn, width: '30px', height: '30px', padding: 0, justifyContent: 'center' }}><BI icon="x-lg" style={{ fontSize: '12px' }} /></button>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '14px' }}>
          {fini ? 'Récapitulatif du passage.' : `Profil ${Math.min(idx + 1, total)} sur ${total} — enregistré au fur et à mesure.`}
        </div>

        {!fini && total > 0 && (
          <div style={{ height: '4px', borderRadius: '2px', background: 'var(--code-bg)', marginBottom: '16px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(idx / total) * 100}%`, background: 'var(--accent)', transition: 'width .2s' }} />
          </div>
        )}

        {corps()}

        <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
          {fini ? (
            <button onClick={onClose} style={btnFort}>Fermer</button>
          ) : (
            <>
              {idx > 0 && <button onClick={() => setIdx(idx - 1)} disabled={busy} style={btn}>Précédent</button>}
              <button onClick={passer} disabled={busy || !p} style={btn}>Passer</button>
              {total - idx > 1 && (
                <button onClick={toutEnregistrer} disabled={busy || !p || !form} style={btn}
                  title="Enregistre ce profil et les suivants avec les valeurs proposées">
                  <BI icon={lot ? 'arrow-repeat' : 'check2-all'} style={lot ? { animation: 'aoi-spin .8s linear infinite' } : undefined} />
                  {lot ? `${lot.nom} (${lot.fait + 1}/${lot.total})…` : `Tout enregistrer (${total - idx})`}
                </button>
              )}
              <button onClick={suivant} disabled={busy || !p || !form} style={{ ...btnFort, opacity: busy ? 0.6 : 1 }}>
                <BI icon={busy ? 'arrow-repeat' : 'check2'} style={busy ? { animation: 'aoi-spin .8s linear infinite' } : undefined} />
                {busy && !lot ? 'Enregistrement…' : idx + 1 < total ? 'Enregistrer et suivant' : 'Enregistrer et terminer'}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
